import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useCallback, useState } from "react";
import { Box, Typography, IconButton, Tooltip, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CommonDrawerLayout from "../../common_components/CommonDrawerLayout";
import CommonLoader from "../../common_components/CommonLoader";
import { useDispatch, useSelector } from "react-redux";
import { fetchCampaignPreview, selectCampaignPreviewData, selectCampaignPreviewLoading, selectCampaignPreviewError, clearCampaignPreview, fetchCampaignAudience, selectCampaignAudienceLoading, selectIncludedListsAndSegments, selectExcludedListsAndSegments, } from "../../redux/campaignPreviewSlice";
import { toPng } from "html-to-image";
import { useRef } from "react";
import TakeActionContent from "../../common_components/TakeActionContent";
import { sendDraftEmailReady } from "../../services/emailService";
import ConfirmActionModal from "../../common_components/ConfirmActionModal";
import { toast } from "../../common_components/Toast/ToastUtils";
import { refreshMarketingCalendar } from "../../redux/marketingCalendarSlice";
import { selectCanDraftEmailApproval, selectSendForApproval } from "../../redux/authSlice";
const sx = {
    wrapper: { height: "100%", display: "flex", flexDirection: "column", p: 1, gap: 1 },
    title: { fontSize: 14, fontWeight: 600, color: "#FF008E" },
    row: { display: "grid", gridTemplateColumns: "200px 15px 1fr", gap: 1 },
    label: { fontWeight: 500, fontSize: 13, color: "#111827" },
    value: { fontSize: 13, color: "#374151" },
    htmlBox: {
        flex: 1,
        border: "1px solid #E5E7EB",
        borderRadius: 1,
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        p: 1,
    },
    tabsContainer: {
        display: "flex",
        backgroundColor: "#F9FAFB",
        borderRadius: "8px",
        p: "4px",
        border: "1px solid #E5E7EB",
        width: "100%",
        gap: "8px"
    },
    tabBtn: {
        flex: 1,
        textAlign: "center",
        py: 1,
        borderRadius: "6px",
        cursor: "pointer",
        fontWeight: 500,
        fontSize: "14px",
        transition: "all 0.3s ease",
    },
    infoContainer: {
        display: "flex",
        flexDirection: "column",
        gap: 1,
        border: "1px solid #E5E7EB",
        backgroundColor: "#F9FAFB",
        padding: 2,
        borderRadius: 1,
    },
    loadingText: {
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: 13,
        color: "#6B7280",
    },
    smsPreviewContainer: {
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        border: "1px solid #E5E7EB",
        backgroundColor: "#F9FAFB",
        padding: 2,
        borderRadius: 1,
        marginTop: 1,
    },
    smsPreviewTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: "#FF008E",
    },
    smsBody: {
        fontSize: 13,
        color: "#374151",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        backgroundColor: "#ffffff",
        padding: 2,
        borderRadius: 1,
        border: "1px solid #E5E7EB",
        lineHeight: 1.6,
    },
};
const InfoRow = ({ label, value }) => {
    if (value === undefined || value === null || value === "")
        return null;
    return (_jsxs(Box, { sx: sx.row, children: [_jsx(Typography, { sx: sx.label, children: label }), _jsx(Typography, { sx: sx.label, children: ":" }), _jsx(Typography, { sx: sx.value, component: "div", children: value })] }));
};
const renderSmsBody = (body) => {
    if (!body)
        return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = body.split(urlRegex);
    return (_jsx(Box, { sx: sx.smsBody, children: parts.map((part, idx) => urlRegex.test(part) ? (_jsx("a", { href: part, target: "_blank", rel: "noopener noreferrer", style: { color: "#FF008E", textDecoration: "underline" }, children: part }, idx)) : (_jsx("span", { children: part }, idx))) }));
};
const extractBody = (html) => {
    const match = html.match(/<body[^>]*>((.|[\n\r])*)<\/body>/im);
    return match ? match[1] : html;
};
const parseListSegments = (text) => {
    if (!text)
        return [];
    const regex = /(.*?)\((\d+)\)/g;
    const result = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
        result.push({ name: match[1].replace(/^,\s*/, "").trim(), count: match[2] });
    }
    return result;
};
const parseExcludedSegments = (text) => {
    if (!text)
        return [];
    return text.split(",").map((item) => item.trim()).filter(Boolean);
};
const ExcludedSegmentsTable = ({ text }) => {
    const data = parseExcludedSegments(text);
    return (_jsx(Box, { sx: { border: "1px solid #E5E7EB", borderRadius: 1, overflow: "hidden", width: "100%" }, children: _jsxs("table", { style: { width: "100%", borderCollapse: "collapse", fontSize: 13 }, children: [_jsx("thead", { children: _jsx("tr", { style: { background: "#F9FAFB" }, children: _jsx("th", { style: { textAlign: "left", padding: "8px" }, children: "List/Segment Name" }) }) }), _jsx("tbody", { children: data.map((name, i) => (_jsx("tr", { children: _jsx("td", { style: { padding: "8px", borderTop: "1px solid #E5E7EB" }, children: name }) }, i))) })] }) }));
};
const ListsSegmentsTable = ({ text }) => {
    const data = parseListSegments(text);
    return (_jsx(Box, { sx: { border: "1px solid #E5E7EB", borderRadius: 1, width: "100%", overflow: "hidden" }, children: _jsxs("table", { style: { width: "100%", borderCollapse: "collapse", fontSize: 13 }, children: [_jsx("thead", { children: _jsxs("tr", { style: { background: "#F9FAFB" }, children: [_jsx("th", { style: { textAlign: "left", padding: "8px" }, children: "List/Segment Name" }), _jsx("th", { style: { textAlign: "right", padding: "8px" }, children: "Count" })] }) }), _jsx("tbody", { children: data.map((item, i) => (_jsxs("tr", { children: [_jsx("td", { style: { padding: "8px", borderTop: "1px solid #E5E7EB" }, children: item.name }), _jsx("td", { style: { padding: "8px", borderTop: "1px solid #E5E7EB", textAlign: "right", fontWeight: 600 }, children: Number(item.count).toLocaleString() })] }, i))) })] }) }));
};
const CampaignPreviewDrawer = ({ open, onClose, rowData, channelKey, clientId, showTakeAction = false, // ✅ Default false
connectionId, campaignId, currentStatus, entityType }) => {
    const dispatch = useDispatch();
    const contentRef = useRef(null);
    const [activeTab, setActiveTab] = useState('information');
    const [confirmSendOpen, setConfirmSendOpen] = useState(false);
    const [confirmSendLoading, setConfirmSendLoading] = useState(false);
    const previewData = useSelector(selectCampaignPreviewData);
    const previewLoading = useSelector(selectCampaignPreviewLoading);
    const previewError = useSelector(selectCampaignPreviewError);
    const audienceLoading = useSelector(selectCampaignAudienceLoading);
    const includedListsAndSegments = useSelector(selectIncludedListsAndSegments);
    const excludedListsAndSegments = useSelector(selectExcludedListsAndSegments);
    const canDraftEmailApproval = useSelector(selectCanDraftEmailApproval);
    const canSendForApproval = useSelector(selectSendForApproval);
    const messageId = rowData?.message_id;
    const audiences = rowData?.audiences || {};
    const content = previewData?.campaignMessage?.data?.content;
    const html = useMemo(() => previewData?.campaignMessage?.included?.[0]?.attributes?.html || "", [previewData]);
    const hasHtml = !!html && html.trim().length > 0;
    const bodyHtml = useMemo(() => extractBody(html), [html]);
    const channel = previewData?.campaignMessage?.data?.channel;
    const isSms = channel === "sms" || channelKey === "sms";
    const hasSmsBody = isSms && content?.body && content.body.trim().length > 0;
    useEffect(() => {
        if (!open || !messageId || !clientId)
            return;
        dispatch(fetchCampaignPreview({ clientId, messageId }));
    }, [open, messageId, clientId, dispatch]);
    useEffect(() => {
        if (!open || !clientId)
            return;
        const hasEntries = (map) => map && (Array.isArray(map) ? map.length > 0 : Object.keys(map).length > 0);
        if (hasEntries(audiences?.included) || hasEntries(audiences?.excluded)) {
            dispatch(fetchCampaignAudience({ clientId, audiences }));
        }
    }, [open, clientId, audiences, dispatch]);
    useEffect(() => {
        if (open)
            setActiveTab('information');
    }, [open]);
    const handleClose = useCallback(() => {
        dispatch(clearCampaignPreview());
        setActiveTab('information');
        onClose();
    }, [dispatch, onClose]);
    const handleDownload = async () => {
        if (!contentRef.current)
            return;
        await new Promise((r) => setTimeout(r, 500));
        const dataUrl = await toPng(contentRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#ffffff" });
        const link = document.createElement("a");
        link.download = `${rowData?.name || "preview"}.png`;
        link.href = dataUrl;
        link.click();
    };
    const AudienceLoadingIndicator = () => (_jsxs(Box, { sx: sx.loadingText, children: [_jsx(CircularProgress, { size: 14, sx: { color: "#FF008E" } }), _jsx("span", { children: "Loading Included Lists and Segments..." })] }));
    const infoRows = useMemo(() => {
        const rows = [];
        if (!isSms) {
            if (content?.subject)
                rows.push({ label: "Subject", value: content.subject });
            if (content?.preview_text)
                rows.push({ label: "Preview Text", value: content.preview_text });
        }
        return rows;
    }, [content, isSms]);
    const showDownloadButton = hasHtml && activeTab === 'preview' && !isSms;
    // ✅ Get tab button style
    const getTabStyle = (tab) => ({
        ...sx.tabBtn,
        color: activeTab === tab ? "#ffffff" : "#6B7280",
        backgroundColor: activeTab === tab ? "#FF008E" : "#F4F4F5",
    });
    const InformationContent = () => (_jsxs(Box, { sx: sx.infoContainer, children: [_jsxs(Typography, { sx: sx.title, children: [isSms ? "SMS" : "Email", " : ", rowData?.name || "Untitled"] }), _jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }, children: infoRows.map((r) => (_jsx(InfoRow, { label: r.label, value: r.value }, r.label))) }), _jsxs(Box, { sx: { mt: 2 }, children: [_jsx(Typography, { sx: { fontWeight: 600, fontSize: 13 }, children: "Include Lists and Segments" }), _jsx(Box, { sx: { mt: 1 }, children: audienceLoading ? (_jsx(AudienceLoadingIndicator, {})) : includedListsAndSegments ? (_jsx(ListsSegmentsTable, { text: includedListsAndSegments })) : (_jsx(Typography, { sx: { fontSize: 13, color: "#6B7280" }, children: "No included lists or segments" })) })] }), _jsxs(Box, { sx: { mt: 2 }, children: [_jsx(Typography, { sx: { fontWeight: 600, fontSize: 13 }, children: "Excluded Lists and Segments" }), _jsx(Box, { sx: { mt: 1 }, children: audienceLoading ? (_jsx(AudienceLoadingIndicator, {})) : excludedListsAndSegments ? (_jsx(ExcludedSegmentsTable, { text: excludedListsAndSegments })) : (_jsx(Typography, { sx: { fontSize: 13, color: "#6B7280" }, children: "No excluded lists or segments" })) })] })] }));
    const SmsPreviewSection = () => (_jsxs(Box, { sx: sx.smsPreviewContainer, children: [_jsx(Typography, { sx: sx.smsPreviewTitle, children: "SMS Preview" }), renderSmsBody(content?.body)] }));
    // ✅ Build tabs array based on props
    const getTabs = () => {
        const tabs = [
            { key: 'information', label: 'Information' }
        ];
        if (hasHtml && !isSms) {
            tabs.push({ key: 'preview', label: 'Preview' });
        }
        if (showTakeAction) {
            tabs.push({ key: 'takeAction', label: 'Take Action' });
        }
        return tabs;
    };
    const handleSendForApproval = () => {
        setConfirmSendOpen(true);
    };
    const confirmSendForApproval = async () => {
        if (!clientId)
            return;
        setConfirmSendLoading(true);
        try {
            // ✅ Fire and forget to avoid slow UI. Klaviyo subject/preview/audience
            // details are resolved server-side (emailController) from campaignId
            // alone — no need to fetch/pass them from here.
            sendDraftEmailReady({
                clientId,
                actionBy: entityType,
                storeName: rowData?.name || "Campaign",
                campaignId: campaignId,
                approvalStatus: rowData?.status
            });
            toast.success("Sent for approval successfully!");
            setConfirmSendLoading(false);
            setConfirmSendOpen(false);
            onClose();
            dispatch(refreshMarketingCalendar());
        }
        catch (err) {
            console.error(err);
            toast.error("Failed to send campaign for approval. Please try again.");
            setConfirmSendLoading(false);
            setConfirmSendOpen(false);
        }
    };
    const tabs = getTabs();
    const showTabs = tabs.length > 1;
    return (_jsxs(_Fragment, { children: [_jsx(CommonDrawerLayout, { open: open, onClose: handleClose, title: isSms ? "SMS Details" : "Campaign Details", width: "md", showOverlay: true, closeOnOverlayClick: true, headerActions: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: "12px" }, children: [showDownloadButton && (_jsx(Tooltip, { title: "Download", children: _jsx("span", { children: _jsx(IconButton, { onClick: handleDownload, disabled: previewLoading, children: _jsx(DownloadIcon, {}) }) }) })), activeTab === 'takeAction' && rowData?.visible_to_client == false && canSendForApproval && (_jsxs("button", { onClick: handleSendForApproval, style: {
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                background: "#FFFCF5",
                                border: "1px solid #E9841D",
                                color: "#E9841D",
                                padding: "6px 16px",
                                borderRadius: "8px",
                                fontWeight: 600,
                                cursor: "pointer",
                                fontSize: "14px",
                            }, children: [_jsx("img", { src: "/assets/head_send.svg", alt: "send", style: { width: 18, height: 18, filter: "brightness(0) saturate(100%) invert(56%) sepia(85%) saturate(1472%) hue-rotate(352deg) brightness(97%) contrast(92%)" } }), "Send For Approval"] }))] }), children: _jsx(Box, { sx: sx.wrapper, children: previewLoading ? (_jsx(Box, { className: "reusable-table-loading", sx: { p: 4, textAlign: "center" }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : previewError ? (_jsx(Typography, { color: "error", fontSize: "14px", children: previewError })) : (_jsxs(_Fragment, { children: [isSms && !showTakeAction && (_jsxs(_Fragment, { children: [_jsx(InformationContent, {}), hasSmsBody && _jsx(SmsPreviewSection, {})] })), isSms && showTakeAction && (_jsxs(_Fragment, { children: [_jsx(Box, { sx: sx.tabsContainer, children: tabs.map((tab) => (_jsx(Box, { onClick: () => setActiveTab(tab.key), sx: getTabStyle(tab.key), children: tab.label }, tab.key))) }), activeTab === 'information' && (_jsxs(_Fragment, { children: [_jsx(InformationContent, {}), hasSmsBody && _jsx(SmsPreviewSection, {})] })), activeTab === 'takeAction' && _jsx(TakeActionContent, { marketingcalenderRefresh: true, currentStatus: currentStatus, client_id: clientId, connection_id: connectionId, data: rowData, hideUpdateForm: !canDraftEmailApproval, campaign_id: campaignId })] })), !isSms && (_jsxs(_Fragment, { children: [showTabs && (_jsx(Box, { sx: sx.tabsContainer, children: tabs.map((tab) => (_jsx(Box, { onClick: () => setActiveTab(tab.key), sx: getTabStyle(tab.key), children: tab.label }, tab.key))) })), activeTab === 'information' && _jsx(InformationContent, {}), activeTab === 'preview' && (_jsx(Box, { className: "hide-scrollbar", sx: sx.htmlBox, children: _jsx("div", { ref: contentRef, dangerouslySetInnerHTML: { __html: bodyHtml } }) })), activeTab === 'takeAction' && _jsx(TakeActionContent, { marketingcalenderRefresh: true, currentStatus: currentStatus, client_id: clientId, connection_id: connectionId, data: rowData, hideUpdateForm: !canDraftEmailApproval, campaign_id: campaignId }), !showTabs && _jsx(InformationContent, {})] }))] })) }) }), _jsx(ConfirmActionModal, { open: confirmSendOpen, loading: confirmSendLoading, onClose: () => setConfirmSendOpen(false), onConfirm: confirmSendForApproval, title: "Send for Approval", description: "Are you sure you want to send this campaign for approval?", confirmText: "Confirm", isDeleteIcon: false, buttonColor: "#FF008E", buttonHoverColor: "#E6007A" })] }));
};
export default CampaignPreviewDrawer;
